import React, { useMemo } from 'react';
import isEqual from 'lodash/isEqual';
import {
  useHighlightingContext,
  withHighlightingContext,
} from '../../../../../components/HighlightingContext';
import {
  PrimaryButton,
  SecondaryButton,
  SquareRemoveButton,
} from '../../../../../components/Button';
import useDashboardSelector from '../../../../../redux/selectorHooks/useDashboardSelector';
import * as DashboardCustomizationsUI from './DashboardCustomizationsUI';
import {
  AddDashboardFilter,
  DashboardFilterView,
} from './DashboardCustomizationFilter';
import DashboardCustomizationDateRange from './DashboardCustomizationDateRange';
import { checkAllAnyPredicates } from './utils';
import { IDashboardEntityPermissions } from '../../../../../utils/hooks/permissionHooks/types';
import KubitTooltip from '../../../../../components/KubitTooltip';
import { isNil } from 'lodash';
import { assertNotNil } from '../../../../../utils/types';
import useDashboardConfigSelector from '../../../../../redux/selectorHooks/useDashboardConfigSelector';
import { dashboardCustomizationReset } from '../../../../../redux/slices/dashboards/dashboardInstance';
import { useAppDispatch } from '../../../../../redux/hooks';
import useExecuteDashboard from './utils/useExecuteDashboard';
import useSetDashboardFilter from './utils/useSetDashboardFilter';

interface IProps {
  dashboardEntityPermissions: IDashboardEntityPermissions;
}

const DashboardCustomizations = ({ dashboardEntityPermissions }: IProps) => {
  const dispatch = useAppDispatch();

  const { dashboardExecuting, handleApplyCustomizations } =
    useExecuteDashboard();
  const { handleSaveCustomizationsAsFilter, handleRemoveDashboardFilter } =
    useSetDashboardFilter();

  const dashboard = useDashboardSelector('dashboard');
  assertNotNil(dashboard);
  const dashboardDateRange = dashboard.filter?.dateRange ?? null;
  const dashboardDatePicker = dashboard.filter?.datePicker ?? null;
  const dashboardFilter = dashboard.filter?.filter ?? null;

  const draftCustomization = useDashboardSelector('draftCustomization');
  const draftFilter = draftCustomization.filter;
  const draftDateRange = draftCustomization.dateRange;
  const draftDatePicker = draftCustomization.datePicker;

  const { logicOps: dashboardLogicOps } = useDashboardConfigSelector('config');

  const { activateHighlighting, deactivateHighlighting } =
    useHighlightingContext();

  const canExecute = useMemo(() => {
    return (
      (!isEqual(dashboardFilter, draftFilter) ||
        !isEqual(dashboardDateRange, draftDateRange) ||
        !isEqual(dashboardDatePicker, draftDatePicker)) &&
      ((draftFilter?.conditions.length ?? 0) > 0 || !isNil(draftDatePicker))
    );
  }, [
    dashboardDatePicker,
    dashboardDateRange,
    dashboardFilter,
    draftDatePicker,
    draftDateRange,
    draftFilter,
  ]);

  const canSetFilters = useMemo(() => {
    // Check if any condition has a predicate value of "Any"
    const hasAnyPredicates =
      !isNil(draftFilter) && checkAllAnyPredicates(draftFilter.conditions);

    return (
      dashboardEntityPermissions.setDashboardFilter &&
      !isEqual(dashboardFilter, draftCustomization.filter) &&
      hasAnyPredicates
    );
  }, [
    draftFilter,
    dashboardEntityPermissions.setDashboardFilter,
    dashboardFilter,
    draftCustomization.filter,
  ]);

  const handleRemoveCustomizations = () => {
    dispatch(dashboardCustomizationReset());
  };

  return (
    <DashboardCustomizationsUI.Container>
      <DashboardCustomizationsUI.Header>
        <DashboardCustomizationsUI.HeaderRow>
          <DashboardCustomizationsUI.Title>
            Dashboard Filter
          </DashboardCustomizationsUI.Title>
          {!isNil(dashboard.filter) &&
          dashboardEntityPermissions.setDashboardFilter ? (
            <SquareRemoveButton
              onClick={handleRemoveDashboardFilter}
              content={'Remove dashboard filter'}
              onMouseEnter={activateHighlighting}
              onMouseLeave={deactivateHighlighting}
            />
          ) : (
            <SquareRemoveButton
              onClick={handleRemoveCustomizations}
              content={'Close filter'}
              onMouseEnter={activateHighlighting}
              onMouseLeave={deactivateHighlighting}
            />
          )}
        </DashboardCustomizationsUI.HeaderRow>
        <DashboardCustomizationsUI.ConditionsContent>
          <DashboardCustomizationsUI.FilterByContainer>
            Filter by
          </DashboardCustomizationsUI.FilterByContainer>
          <DashboardCustomizationDateRange />
          {draftFilter && draftFilter.conditions.length > 0 && (
            <DashboardFilterView
              filter={draftFilter}
              logicOps={dashboardLogicOps}
              includeAnyValue={dashboardEntityPermissions.setDashboardFilter}
            />
          )}
          <AddDashboardFilter
            includeAnyValue={dashboardEntityPermissions.setDashboardFilter}
          />
        </DashboardCustomizationsUI.ConditionsContent>
      </DashboardCustomizationsUI.Header>
      <DashboardCustomizationsUI.Footer>
        <DashboardCustomizationsUI.FooterContent>
          {canSetFilters ? (
            <KubitTooltip
              noOpenDelay={true}
              content="Set filters to the dashboard."
            >
              <SecondaryButton
                type="button"
                disabled={!canSetFilters}
                onClick={handleSaveCustomizationsAsFilter}
                trackContext="dashboards"
                trackAction="set default filter"
              >
                Set Default Filter
              </SecondaryButton>
            </KubitTooltip>
          ) : (
            <PrimaryButton
              type="button"
              disabled={dashboardExecuting || !canExecute}
              onClick={handleApplyCustomizations}
              loading={dashboardExecuting}
              trackContext="dashboards"
              trackAction="execute"
            >
              Execute
            </PrimaryButton>
          )}
        </DashboardCustomizationsUI.FooterContent>
      </DashboardCustomizationsUI.Footer>
    </DashboardCustomizationsUI.Container>
  );
};

export default withHighlightingContext(DashboardCustomizations);
